/**
 * Copyright © 2026 Adnuntius AS.
 */
import {BsDatepickerConfig, BsDatepickerModule, BsLocaleService} from 'ngx-bootstrap/datepicker';
import {Component, Inject, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';

import template from './booking-calendar-page.component.html';
import {APP_SHARED} from '../common/app-shared';
import {applyNetworkCalendarWeekStart, toBsDateInputFormat} from '../common/directives/form-controls/bs-datepicker-locale';
import {networkDayFromPickerDate, pickerDateFromNetworkDay} from '../common/locale/network-date-picker-utils';
import {AppLocaleService} from '../common/locale/app-locale.service';
import {getIntlLocaleData, AngularLocaleData} from '../common/locale/intl-locale-data';
import {BookingCalendarApiService} from './booking-calendar-api.service';
import {AdnTimeShifterService} from '../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {StateService} from '@uirouter/core';

type BookingMetric = 'impressions' | 'rendered' | 'clicks' | 'viewables' | 'visibles' | 'conversions' | 'budget';

@Component({
  standalone: true,
  selector: 'adn-booking-calendar-page',
  imports: [...APP_SHARED, FormsModule, TranslateModule, BsDatepickerModule],
  template
})
export class BookingCalendarPageComponent implements OnInit {
  readonly metricKeys: BookingMetric[] = ['impressions', 'rendered', 'clicks', 'viewables', 'visibles', 'conversions', 'budget'];
  readonly metricLabels: Record<BookingMetric, string> = {
    impressions: 'Impressions',
    rendered: 'Rendered',
    clicks: 'Clicks',
    viewables: 'Viewables',
    visibles: 'Visibles',
    conversions: 'Conversions',
    budget: 'Budget'
  };
  readonly chunkOptions = ['DAY', 'WEEK', 'MONTH'];

  compareReachAnalysis = false;
  isAxAdvertiser: boolean;
  columns: Record<BookingMetric, boolean> = this.metricState(true);
  chunkMaxes: Record<BookingMetric, number> = this.metricState(0);
  chunk = 'DAY';
  startDate: Date;
  endDate: Date;
  bookings: any = {};
  hasResults = 0;
  requested = false;
  loading = false;
  bsConfig: Partial<BsDatepickerConfig>;
  private readonly localeData: AngularLocaleData;

  constructor(
    private readonly bookingCalendarApi: BookingCalendarApiService,
    @Inject(LocalUserPermissions) LocalUserPermissions: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    private readonly appLocale: AppLocaleService,
    private readonly $state: StateService,
    private readonly bsLocaleService: BsLocaleService,
    private readonly translate: TranslateService
  ) {
    this.localeData = getIntlLocaleData(this.appLocale.getLocaleId());
    this.isAxAdvertiser = LocalUserPermissions.isAxAdvertiser();
    const networkTz = this.LocalNetworkProfile.get('timeZone') || 'utc';
    const networkNow = this.adnTimeShifter.getMomentInNetworkTimezone();
    this.startDate = pickerDateFromNetworkDay(networkNow.format('YYYY-MM-DD'), networkTz, 'start');
    this.endDate = pickerDateFromNetworkDay(
      networkNow.clone().add(1, 'month').endOf('month').format('YYYY-MM-DD'),
      networkTz,
      'end'
    );
    this.bsConfig = this.createBsConfig();
  }

  ngOnInit(): void {
    applyNetworkCalendarWeekStart(
      this.bsLocaleService,
      this.LocalNetworkProfile.getDefaults()?.startDayCalendar,
      this.localeData
    );
  }

  getBookings(): void {
    this.requested = true;
    this.loading = true;
    this.bookings = {};
    this.hasResults = 0;
    this.chunkMaxes = this.metricState(0);

    this.bookingCalendarApi.getBookings({
      startDate: this.formatDate(this.startDate),
      endDate: this.formatDate(this.endDate),
      chunkSize: this.chunk
    }).subscribe({next: (data) => {
      data.chunks = this.normalizeCollection(data.chunks);
      _.forEach(data.chunks, (calendarChunk) => {
        calendarChunk.lineItems = this.normalizeCollection(calendarChunk.lineItems);
      });
      this.bookings = data;
      this.updateMetricState();
      this.loading = false;
    }, error: () => {
      this.bookings = {};
      this.loading = false;
    }});
  }

  metricValue(source: any, metric: BookingMetric): any {
    return _.get(source, metric, 0) || 0;
  }

  metricAmount(source: any, metric: BookingMetric): number {
    const value = this.metricValue(source, metric);
    return _.get(value, 'amount', value) || 0;
  }

  progressWidth(chunk: any, metric: BookingMetric): number {
    const maximum = this.chunkMaxes[metric];
    return maximum > 0 ? 100 * this.metricAmount(chunk, metric) / maximum : 0;
  }

  lineItemHref(lineItem: any): string {
    return this.$state.href('app.adv.line-items.line-item', {id: lineItem?.id});
  }

  private updateMetricState(): void {
    _.forEach(this.metricKeys, (metric) => {
      this.columns[metric] = this.metricAmount(this.bookings.total, metric) > 0;
    });

    _.forEach(this.bookings.chunks, (chunk) => {
      _.forEach(this.metricKeys, (metric) => {
        this.chunkMaxes[metric] = Math.max(this.chunkMaxes[metric], this.metricAmount(chunk, metric));
      });
    });

    this.hasResults = _.reduce(this.metricKeys, (sum, metric) => this.columns[metric] ? sum + 1 : sum, 0);
  }

  private metricState<T>(value: T): Record<BookingMetric, T> {
    return _.reduce(this.metricKeys || ['impressions', 'rendered', 'clicks', 'viewables', 'visibles', 'conversions', 'budget'], (state, metric) => {
      state[metric as BookingMetric] = value;
      return state;
    }, {} as Record<BookingMetric, T>);
  }

  private formatDate(date: Date): string {
    return networkDayFromPickerDate(date, this.adnTimeShifter);
  }

  private normalizeCollection(collection: any): any[] {
    return _.isArray(collection) ? collection : (_.isObject(collection) ? _.values(collection) : []);
  }

  private createBsConfig(): Partial<BsDatepickerConfig> {
    return {
      dateInputFormat: toBsDateInputFormat(this.localeData.DATETIME_FORMATS.shortDate),
      showTodayButton: true,
      showClearButton: true,
      todayPosition: 'left',
      clearPosition: 'right',
      todayButtonLabel: this.translate.instant('button.today'),
      clearButtonLabel: this.translate.instant('button.clear')
    };
  }
}
